import * as React from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Link,
  List,
  ListItem,
  Modal,
  Slider,
  Stack,
  TextField,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import { makeStyles } from "@mui/styles";
import { Add, AutoFixHigh, Cancel, Info, PermDataSetting, SignalCellular1Bar, SignalCellular3Bar, SignalCellular4Bar, SignalCellularNull } from "@mui/icons-material";
import { useContext, useEffect, useState } from "react";
import {
  getTrainingDatasets,
  uploadTrainingDataset,
} from "../../services/TrainingDatasets";
import {
  activateRevenueTargetingModel,
  deactivateRevenueTargetingModel,
  getRevenueTargetingModel,
  saveRevenueTargetingModel,
  updateTrainingDatasets,
} from "../../services/RevenueTargetingModels";
import AuthContext from "../../context/AuthContext";
import { getEditPageDefinitions } from "../../services/Definitions";
import SnackbarContext from "../../context/SnackbarContext";
import { getCompanies } from "../../services/Companies";

const trainingDatasetTooltip =
  "Closed opportunity data used to train the Revenue Targeting Model (RTM). The RTM scores prospective companies based on how well they align with your best customers.";
const contactFiltersTooltip =
  "Filter matched contacts by seniority and function.";
const companyFiltersTooltip =
  "Filter matched companies by country and business type.";
const motivationSignalsTooltip =
  "A set of signals RevOptimal will track to determine companies registering purchase motivation in real-time.";

const useStyles = makeStyles({
  downloadCSVButton: {
    "&.MuiButton-root": {
      marginRight: "12px",
    },
  },
  infoIcon: {
    "&.MuiSvgIcon-root": {
      fontSize: "20px",
      position: "absolute",
      padding: "3px 0 0 8px",
    },
  },
  infoIconButton: {
    padding: 0,
  },
  headerBox: {
    color: "#95a2b4",
    fontSize: "14px",
    fontWeight: "bold",
  },
  sectionHeader: {
    "&.MuiTypography-root": {
      color: "#3d4849",
      fontSize: "16px",
      fontWeight: 700,
      padding: "20px",
    },
  },
  sectionHeaderNoPadding: {
    "&.MuiTypography-root": {
      color: "#3d4849",
      fontSize: "16px",
      fontWeight: 700
    }
  },
  section: {
    fontSize: "14px",
    margin: "20px 40px 40px 40px",
  },
  editBox: {
    height: "calc(100vh - 176px)",
    color: "#3d4849",
    background: "#fdfdfd",
    borderRadius: "3px",
    padding: "12px",
  },
  launchButton: { width: 160, fontWeight: "bold" },
  saveButton: {
    width: 160,
    "&.MuiButton-root": {
      fontWeight: "bold",
      background: "#07A89E",
      marginLeft: "12px",
      "&:hover": {
        background: "#0A8F87",
      },
    },
  },
  deactivateButton: {
    width: 180,
    "&.MuiButton-root": {
      fontWeight: "bold",
      background: "#F22F2F",
      "&:hover": {
        background: "#CF0909",
      },
    },
  },
  cancelButton: {
    "&.MuiButton-root": {
      fontWeight: "bold",
      background: "#F22F2F",
      "&:hover": {
        background: "#CF0909",
      },
    },
  },
  csvLink: {
    fontWeight: "bold",
  },
  trainingDatasetCardContent: {
    margin: "20px 40px 0 40px",
  },
  trainingDatasetLink: {
    "&.MuiTypography-root": {
      color: "#95a2b4",
      fontWeight: "bold",
      "&:hover": {
        color: "#009cf8",
      },
    },
  },
  trainingDatasetListItem: {
    "&.MuiListItem-root": {
      padding: "4px 0 0 0",
    },
  },
  launchTitle: {
    fontWeight: "bold",
    fontSize: "20px",
    color: "#3d4849",
  },
});

const createModalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 280,
  backgroundColor: "background.paper",
  borderRadius: "6px",
  boxShadow: 24,
  p: 4,
};

const createAiModalStyles = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  backgroundColor: "background.paper",
  borderRadius: "6px",
  boxShadow: 24,
  p: 4,
};

export default function EditRevenueTargetingModel({
  revenueTargetingModelID,
  getModels,
}) {
  const classes = useStyles();

  const [isLoading, setIsLoading] = useState(true);
  const [definitionsLoading, setDefinitionsLoading] = useState(true);
  const [definitions, setDefinitions] = useState({});
  const [name, setName] = useState("");
  const [isModelActive, setIsModelActive] = useState(false);
  const [goal, setGoal] = useState("");
  const [activateBtnLoading, setActivateBtnLoading] = useState(false);
  const [deactivateBtnLoading, setDeactivateBtnLoading] = useState(false);
  const [saveBtnLoading, setSaveBtnLoading] = useState(false);
  const [trainingDatasets, setTrainingDatasets] = useState([]);
  const [contactSeniorityFilters, setContactSeniorityFilters] = useState([]);
  const [contactTitleFilters, setContactTitleFilters] = useState([]);
  const [contactFunctionFilters, setContactFunctionFilters] = useState([]);
  const [countryFilters, setCountryFilters] = useState([]);
  const [companyTypeFilters, setCompanyTypeFilters] = useState([]);
  const [industryFilters, setIndustryFilters] = useState([]);
  const [employeeBandFilters, setEmployeeBandFilters] = useState([]);
  const [employeeBandMarks, setEmployeeBandMarks] = useState([]);
  const [revenueBandFilters, setRevenueBandFilters] = useState([]);
  const [revenueBandMarks, setRevenueBandMarks] = useState([]);
  const [maxSignals, setMaxSignals] = useState(0);
  const [websitePages, setWebsitePages] = useState([]);
  const [competitorInterest, setCompetitorInterest] = useState([]);
  const [eventsAndWebinars, setEventsAndWebinars] = useState([]);
  const [functionalInvestments, setFunctionalInvestments] = useState([]);
  const [painPointKeywords, setPainPointKeywords] = useState([]);
  const [solutionResearchKeywords, setSolutionResearchKeywords] = useState([]);
  const [modelTrainingDatasets, setModelTrainingDatasets] = useState([]);
  const [open, setOpen] = useState(false);

  const [aiOpen, setAiOpen] = useState(false);
  const [aiWebsitePages, setAiWebsitePages] = useState([]);
  const [aiCompetitorInterest, setAiCompetitorInterest] = useState([]);
  const [aiEventsAndWebinars, setAiEventsAndWebinars] = useState([]);
  const [aiFunctionalInvestments, setAiFunctionalInvestments] = useState([]);
  const [aiPainPointKeywords, setAiPainPointKeywords] = useState([]);
  const [aiSolutionResearchKeywords, setAiSolutionResearchKeywords] = useState([]);

  const [mostRecentMotivationSignals, setMostRecentMotivationSignals] = useState([]);
  const [presentMotivationSignals, setPresentMotivationSignals] = useState([]);

  const auth = useContext(AuthContext);
  const snackbar = useContext(SnackbarContext);

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  useEffect(() => {
    setDefinitionsLoading(true);
    getEditPageDefinitions(auth.token)
      .then((response) => {
        if (
          response.countries &&
          response.business_types &&
          response.contact_seniorities &&
          response.contact_functions &&
          response.industries &&
          response.employee_bands &&
          response.revenue_bands
        ) {
          setDefinitions(response);
        } else {
          throw new Error("invalid response from server: definitions".repl);
        }
      })
      .catch(() => {
        snackbar.createError("Error retrieving definitions.");
      })
      .finally(() => {
        setDefinitionsLoading(false);
      });
  }, []);

  useEffect(() => {
    let employeeMarks = []
    definitions.employee_bands?.forEach((band, index) => {
      let markLabel = band.split('to')[0].replace('+','').replace(/\s+/g, '')
      if (markLabel.length > 1) {
        markLabel = markLabel.slice(0,markLabel.length-1) + '0'
      }
      employeeMarks.push({
        value: index,
        label: markLabel,
        band: band
      });
    });
    if (employeeMarks.length > 0) {
      employeeMarks.push({
        value: definitions.employee_bands.length,
        label: definitions.employee_bands[definitions.employee_bands.length-1],
        band: definitions.employee_bands[definitions.employee_bands.length-1]
      })
      setEmployeeBandMarks(employeeMarks);
    }

    let revenueMarks = []
    definitions.revenue_bands?.forEach((band, index) => {
      revenueMarks.push({
        value: index,
        label: band.split('to')[0].replace('+',''),
        band: band
      });
    });
    if (revenueMarks.length > 0) {
      revenueMarks.push({
        value: definitions.revenue_bands.length,
        label: definitions.revenue_bands[definitions.revenue_bands.length-1],
        band: definitions.revenue_bands[definitions.revenue_bands.length-1]
      })
      setRevenueBandMarks(revenueMarks);
    }
  }, [definitions])

  useEffect(() => {
    if (revenueTargetingModelID) {
      setOpen(false);
      setIsLoading(true);
      getRevenueTargetingModel(auth.token, revenueTargetingModelID)
        .then((response) => {
          const model = response.revenue_targeting_models[0];
          setName(model.name);
          setIsModelActive(model.active);
          setGoal(model.goal);
          setContactSeniorityFilters(model.contact_filters.seniorities);
          setContactTitleFilters(model.contact_filters.titles);
          setContactFunctionFilters(model.contact_filters.functions);
          setCountryFilters(model.company_filters.countries);
          setCompanyTypeFilters(model.company_filters.business_types);
          setIndustryFilters(model.company_filters.industries);
          setEmployeeBandFilters(model.company_filters.employee_bands);
          setRevenueBandFilters(model.company_filters.revenue_bands);
          setMaxSignals(model.max_motivation_signals);
          setModelTrainingDatasets(model.training_datasets);

          setWebsitePages(model.motivation_signals.filter(signal => signal.type === 'website_page'));
          setCompetitorInterest(model.motivation_signals.filter(signal => signal.type === 'competitor_interest'));
          setFunctionalInvestments(model.motivation_signals.filter(signal => signal.type === 'functional_investment'));
          setEventsAndWebinars(model.motivation_signals.filter(signal => signal.type === 'event_and_webinar'));
          setPainPointKeywords(model.motivation_signals.filter(signal => signal.type === 'pain_point_keyword'));
          setSolutionResearchKeywords(model.motivation_signals.filter(signal => signal.type === 'solution_research_keyword'));
          
          setAiWebsitePages(model.ai_suggested_motivation_signals.filter(signal => signal.type === 'website_page'));
          setAiCompetitorInterest(model.ai_suggested_motivation_signals.filter(signal => signal.type === 'competitor_interest'));
          setAiFunctionalInvestments(model.ai_suggested_motivation_signals.filter(signal => signal.type === 'functional_investment'));
          setAiEventsAndWebinars(model.ai_suggested_motivation_signals.filter(signal => signal.type === 'event_and_webinar'));
          setAiPainPointKeywords(model.ai_suggested_motivation_signals.filter(signal => signal.type === 'pain_point_keyword'));
          setAiSolutionResearchKeywords(model.ai_suggested_motivation_signals.filter(signal => signal.type === 'solution_research_keyword'));
        })
        .catch((error) => {
          snackbar.createError("Error retrieving Revenue Targeting Model.");
        })
        .finally(() => {
          setIsLoading(false);
          setMotivationSignalLists();
        });
    }
  }, [revenueTargetingModelID]);

  const setMotivationSignalLists = () => {
    if (revenueTargetingModelID) {
      const thresholdDate = new Date();
      thresholdDate.setDate(thresholdDate.getDate() - 7);

      getCompanies(auth.token, revenueTargetingModelID, 1, 1)
      .then((response) => {
        if (response.total_objects) {
          getCompanies(auth.token, revenueTargetingModelID, response.total_objects, 1)
          .then((response) => {
            let recentSignals = []
            let allSignals = []

            response.companies.forEach(company => {
              const matchDate = new Date(company.matched_datetime);
              let companySignals = []
              company.motivation_signals.forEach(signal => {
                companySignals.push(signal.value);
              });

              if (matchDate.getTime() > thresholdDate.getTime()) {
                recentSignals = [...recentSignals, ...companySignals]
              } else {
                allSignals = [...allSignals, ...companySignals]
              };
            });

            let recentSignalsLowerCase = recentSignals.map(word => word.toLowerCase());
            let allSignalsLowerCase = allSignals.map(word => word.toLowerCase());

            setMostRecentMotivationSignals(recentSignalsLowerCase);
            setPresentMotivationSignals(allSignalsLowerCase);
          });
        };
      });
    };
  };


  useEffect(() => {
    if (modelTrainingDatasets.length > 0) {
      getTrainingDatasets(auth.token, modelTrainingDatasets)
        .then((response) => {
          setTrainingDatasets(response.training_datasets);
        })
        .catch((error) => {
          snackbar.createError("Error retrieving training datasets.");
        });
    } else {
      setTrainingDatasets([]);
    }
  }, [modelTrainingDatasets]);

  const handleDeactivate = () => {
    setDeactivateBtnLoading(true);
    deactivateRevenueTargetingModel(auth.token, revenueTargetingModelID)
      .then(() => {
        setIsModelActive(false);
        getModels();
      })
      .catch((error) => {
        snackbar.createError("Error deactivating model.");
      })
      .finally(() => {
        setDeactivateBtnLoading(false);
      });
  };

  const handleActivate = () => {
    setActivateBtnLoading(true);
    activateRevenueTargetingModel(auth.token, revenueTargetingModelID)
      .then(() => {
        setIsModelActive(true);
        getModels();
      })
      .catch((error) => {
        snackbar.createError("Error activating model.");
      })
      .finally(() => {
        setActivateBtnLoading(false);
      });
  };

  const handleSave = () => {
    setSaveBtnLoading(true);
    saveRevenueTargetingModel(auth.token, {
      id: revenueTargetingModelID,
      company_filters: {
        countries: countryFilters,
        business_types: companyTypeFilters,
        industries: industryFilters,
        employee_bands: employeeBandFilters,
        revenue_bands: revenueBandFilters
      },
      contact_filters: {
        seniorities: contactSeniorityFilters,
        titles: contactTitleFilters,
        functions: contactFunctionFilters,
      },
      motivation_signals: [
        ...websitePages,
        ...competitorInterest,
        ...functionalInvestments,
        ...eventsAndWebinars,
        ...painPointKeywords,
        ...solutionResearchKeywords
      ]
    })
      .then((response) => {
        if (response.ok) {
          snackbar.createSuccess("Model saved!")
        } else {
          if (websitePages.length + competitorInterest.length + functionalInvestments.length + eventsAndWebinars.length + painPointKeywords.length + solutionResearchKeywords.length) {
            snackbar.createError("Too many motivation signals.")
          } else {
            snackbar.createError("Error saving model.")
          }
        }
      })
      .catch((error) => {
        snackbar.createError("Error saving model.");
      })
      .finally(() => {
        setSaveBtnLoading(false);
      });
  };

  const handleCSVChange = (e) => {
    uploadTrainingDataset(
      auth.token,
      auth.userAccountID,
      e.target.files[0],
      e.target.files[0].name
    )
      .then((response) => {
        if (response.id) {
          snackbar.createSuccess("Training dataset uploaded!");

          const newModelTrainingDatasets = [
            ...modelTrainingDatasets,
            response.id,
          ];

          updateTrainingDatasets(
            auth.token,
            revenueTargetingModelID,
            newModelTrainingDatasets
          ).then((response) => {
            if (response.id) {
              setModelTrainingDatasets(newModelTrainingDatasets);
            } else {
              throw new Error(
                "invalid response from server: updating training datasets"
              );
            }
          });
        } else {
          throw new Error(
            "invalid response from server: uploading training dataset"
          );
        }
      })
      .catch((error) => {
        snackbar.createError("Error uploading training dataset.");
      });
  };

  const handleRemoveTrainingDataset = (id) => {
    const newModelTrainingDatasets = modelTrainingDatasets.filter(
      (i) => i !== id
    );
    updateTrainingDatasets(
      auth.token,
      revenueTargetingModelID,
      newModelTrainingDatasets
    )
      .then((response) => {
        if (response.id) {
          snackbar.createSuccess("Training dataset deleted!");
          setModelTrainingDatasets(newModelTrainingDatasets);
        } else {
          throw new Error(
            "invalid response from server: updating training datasets"
          );
        }
      })
      .catch((error) => {
        snackbar.createError("Error deleting training dataset.");
      });
  };

  const handleRevenueSliderChange = (event, newValue, activeThumb) => {
    if (activeThumb === 0) {
      setRevenueBandFilters(definitions.revenue_bands.slice(Math.min(newValue[0], newValue[1] - 1),newValue[1]))
    } else {
      setRevenueBandFilters(definitions.revenue_bands.slice(newValue[0],Math.max(newValue[1], newValue[0] + 1)))
    }
  };

  const handleEmployeeBandChange = (event, newValue, activeThumb) => {
    if (activeThumb === 0) {
      setEmployeeBandFilters(definitions.employee_bands.slice(Math.min(newValue[0], newValue[1] - 1),newValue[1]));
    } else {
      setEmployeeBandFilters(definitions.employee_bands.slice(newValue[0],Math.max(newValue[1], newValue[0] + 1)));
    }
  }

  const handleAiSuggestionsOpen = () => {
    setAiOpen(true);
  };

  const handleAddAiSignal = (newSignal, addFunc, removeFunc) => {
    newSignal.ai_suggested = true
    addFunc((currentArray) => [...currentArray, newSignal]);
    removeFunc((currentArray) => currentArray.filter((currentArrayElement) => currentArrayElement !== newSignal));
  }

  function AIIcon() {
    return (
      <React.Fragment>
        <Tooltip
          title={"AI Suggested Signal"}
          arrow
          placement="bottom"
        >
          <AutoFixHigh sx={{fontSize:"22px",ml:"3px"}}/>
        </Tooltip>
      </React.Fragment>
    )
  };

  function CalculatingSignalStrength() {
    return (
      <React.Fragment>
        <Tooltip
          title={"Global Signal Strength: Calculating"}
          arrow
          placement="bottom"
        >
          <PermDataSetting sx={{fontSize:"22px",ml:"3px"}}/>
        </Tooltip>
      </React.Fragment>
    )
  };

  function ZeroSignalStrength() {
    return (
      <React.Fragment>
        <Tooltip
          title={"Global Signal Strength: None"}
          arrow
          placement="bottom"
        >
          <SignalCellularNull sx={{fontSize:"22px",ml:"3px"}}/>
        </Tooltip>
      </React.Fragment>
    )
  };

  function LowSignalStrength() {
    return (
      <React.Fragment>
        <Tooltip
          title={"Global Signal Strength: Low"}
          arrow
          placement="bottom"
        >
          <SignalCellular1Bar sx={{fontSize:"22px",ml:"3px"}}/>
        </Tooltip>
      </React.Fragment>
    )
  };

  function MediumSignalStrength() {
    return (
      <React.Fragment>
        <Tooltip
          title={"Global Signal Strength: Medium"}
          arrow
          placement="bottom"
        >
          <SignalCellular3Bar sx={{fontSize:"22px",ml:"3px"}}/>
        </Tooltip>
      </React.Fragment>
    )
  };

  function HighSignalStrength() {
    return (
      <React.Fragment>
        <Tooltip
          title={"Global Signal Strength: High"}
          arrow
          placement="bottom"
        >
          <SignalCellular4Bar sx={{fontSize:"22px",ml:"3px"}}/>
        </Tooltip>
      </React.Fragment>
    )
  };

  if (auth.userAccountID === 26) {
    let website_pages = [
      {
        title: 'revoptimal.com',
        color: 'performantSignal',
        ai_suggested: false,
        strength: undefined
      },
      {
        title: 'revoptimal.io',
        color: 'performantSignal',
        ai_suggested: false,
        strength: undefined
      },
      {
        title: 'revoptimal.co',
        color: 'presentSignal',
        ai_suggested: false,
        strength: undefined
      },
      {
        title: 'revoptimal.com/pricing',
        color: 'presentSignal',
        ai_suggested: false,
        strength: undefined
      },
      {
        title: 'revoptimal.io/pricing',
        color: 'presentSignal',
        ai_suggested: false,
        strength: undefined
      },
      {
        title: 'revoptimal.co/pricing',
        color: 'presentSignal',
        ai_suggested: false,
        strength: undefined
      }
    ];
    let our_competitors = [
      {
        title: 'bombora.com',
        color: 'performantSignal',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'apollo.io',
        color: 'performantSignal',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'keyplay.io',
        color: 'performantSignal',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'closefactor.com',
        color: 'performantSignal',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'madkudu.com',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'terminus.com',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'adapt.io',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'leadiq.com',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'truebase.io',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'cognism.com',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <ZeroSignalStrength/>
      },
      {
        title: 'rocketreach.co',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <ZeroSignalStrength/>
      },
      {
        title: 'closelyhq.com',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <CalculatingSignalStrength/>
      }
    ];
    let events_and_webinars = [
      {
        title: 'B2B Marketing Exchange',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'Leadscon',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      }
    ];
    let functional_investment = [
      {
        title: 'RevOps',
        color: 'performantSignal',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'Revenue Operations',
        color: 'performantSignal',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'Sales Development',
        color: 'performantSignal',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'Demand Generation',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'Sales Enablement',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'Demand Gen',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      }
    ];
    let pain_points = [
      {
        title: 'churn prevention',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'prospect conversion',
        color: 'neutralDark',
        ai_suggested: true,
        strength: <CalculatingSignalStrength/>
      },
      {
        title: 'lead conversion',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <ZeroSignalStrength/>
      }
    ];
    let solution_research = [
      {
        title: 'sales engagement software',
        color: 'performantSignal',
        ai_suggested: true,
        strength: <HighSignalStrength/>
      },
      {
        title: 'account based marketing',
        color: 'performantSignal',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'account intelligence',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'intent data',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'account based sales',
        color: 'presentSignal',
        ai_suggested: true,
        strength: <LowSignalStrength/>
      },
      {
        title: 'ABM',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'sales intelligence',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'account based engagement',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      }
    ];
    let ai_suggestions_pain_71 = [
      {
        title: 'revenue management',
        strength: 2
      },
      {
        title: 'revenue optimization',
        strength: 2
      },
      {
        title: 'saas conversion',
        strength: 2
      },
      {
        title: 'sales performance management',
        strength: 1
      }
    ];
    let ai_suggestions_solution_71 = [
      {
        title: 'ai sales',
        strength: 3
      },
      {
        title: 'consumer intent',
        strength: 3
      },
      {
        title: 'revenue accounting',
        strength: 3
      },
      {
        title: 'buyer intent',
        strength: 2
      },
      {
        title: 'zoominfo intent',
        strength: 2
      },
      {
        title: 'ai marketing',
        strength: 2
      },
      {
        title: 'behavioral intent',
        strength: 1
      },
      {
        title: 'intent data marketing',
        strength: 1
      },
      {
        title: 'sales consulting',
        strength: 1
      },
      {
        title: 'employee engagement software',
        strength: 1
      },
      {
        title: 'patient engagement software',
        strength: 1
      }
    ];

    let website_pages_106 = [
      {
        title: 'revoptimal.com/revoptimal-vs-6sense',
        color: 'presentSignal',
        ai_suggested: false,
        strength: undefined
      },
      {
        title: 'revoptimal.com/pricing',
        color: 'presentSignal',
        ai_suggested: false,
        strength: undefined
      },
      {
        title: 'revoptimal.co/revoptimal-vs-6sense',
        color: 'neutralDark',
        ai_suggested: false,
        strength: undefined
      },
      {
        title: 'revoptimal.io/pricing',
        color: 'neutralDark',
        ai_suggested: false,
        strength: undefined
      },
      {
        title: 'revoptimal.co/pricing',
        color: 'neutralDark',
        ai_suggested: false,
        strength: undefined
      }
    ];
    let our_competitors_106 = [
      {
        title: 'keyplay.io',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'bombora.com',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'apollo.io',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'closefactor.com',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'madkudu.com',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'terminus.com',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'adapt.io',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'leadiq.com',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'truebase.io',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'cognism.com',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <ZeroSignalStrength/>
      },
      {
        title: 'rocketreach.co',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <ZeroSignalStrength/>
      },
      {
        title: 'closelyhq.com',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <CalculatingSignalStrength/>
      }
    ];
    let pain_point_106 = [
      {
        title: 'prospect conversion',
        color: 'neutralDark',
        ai_suggested: true,
        strength: <LowSignalStrength/>
      },
      {
        title: 'lead conversion',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      }
    ];
    let solution_research_106 = [
      {
        title: 'intent data providers',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'sales engagement software',
        color: 'neutralDark',
        ai_suggested: true,
        strength: <HighSignalStrength/>
      },
      {
        title: 'account intelligence',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'sales intelligence',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      }
    ];
    let ai_suggestions_pain_106 = [
      {
        title: 'saas conversion',
        strength: 2
      },
      {
        title: 'revenue optimization',
        strength: 2
      },
      {
        title: 'revenue management',
        strength: 2
      },
      {
        title: 'sales performance management',
        strength: 1
      }
    ];
    let ai_suggestions_solution_106 = [
      {
        title: 'ai sales',
        strength: 3
      },
      {
        title: 'consumer intent',
        strength: 3
      },
      {
        title: 'revenue accounting',
        strength: 3
      },
      {
        title: 'ai marketing',
        strength: 2
      },
      {
        title: 'buyer intent',
        strength: 2
      },
      {
        title: 'zoominfo intent',
        strength: 2
      },
      {
        title: 'behavioral intent',
        strength: 1
      },
      {
        title: 'employee engagement software',
        strength: 1
      },
      {
        title: 'intent data marketing',
        strength: 1
      },
      {
        title: 'patient engagement software',
        strength: 1
      },
      {
        title: 'sales consulting',
        strength: 1
      }
    ];

    let website_pages_107 = [
      {
        title: 'revoptimal.com/knowledge-base',
        color: 'presentSignal',
        ai_suggested: false,
        strength: undefined
      },
      {
        title: 'revoptimal.io/knowledge-base',
        color: 'neutralDark',
        ai_suggested: false,
        strength: undefined
      },
      {
        title: 'revoptimal.co/revoptimal-vs-6sense',
        color: 'neutralDark',
        ai_suggested: false,
        strength: undefined
      },
      {
        title: 'revoptimal.co/knowledge-base',
        color: 'neutralDark',
        ai_suggested: false,
        strength: undefined
      }
    ];
    let our_competitors_107 = [];
    let events_and_webinars_107 = [
      {
        title: 'B2B Marketing Exchange',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'Leadscon',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      }
    ];
    let functional_investment_107 = [
      {
        title: 'RevOps',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'Revenue Operations',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'Sales Enablement',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'Demand Generation',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'Sales Development',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'Demand Gen',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      }
    ];
    let pain_point_107 = [
      {
        title: 'churn prevention',
        color: 'presentSignal',
        ai_suggested: true,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'prospect conversion',
        color: 'neutralDark',
        ai_suggested: true,
        strength: <LowSignalStrength/>
      },
      {
        title: 'lead conversion',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      }
    ];
    let solution_research_107 = [
      {
        title: 'upsell opportunities',
        color: 'presentSignal',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'sales engagement software',
        color: 'neutralDark',
        ai_suggested: true,
        strength: <HighSignalStrength/>
      },
      {
        title: 'account based marketing',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <HighSignalStrength/>
      },
      {
        title: 'account based sales',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'sales intelligence',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      },
      {
        title: 'account based engagement',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <LowSignalStrength/>
      },
      {
        title: 'account intelligence',
        color: 'neutralDark',
        ai_suggested: false,
        strength: <MediumSignalStrength/>
      }
    ];

    let autocompletes = [];
    let ai_suggestions = []
    if (revenueTargetingModelID === 71) {
      autocompletes = [
        {
          id: 'signals-pages',
          value: website_pages, 
          label: 'Website Pages'
        },
        {
          id: 'signals-competitors',
          value: our_competitors,
          label: 'Competitor Interest'
        },
        {
          id: 'signals-events',
          value: events_and_webinars,
          label: 'Events and Webinars'
        },
        {
          id: 'signals-jobs',
          value: functional_investment,
          label: 'Functional Investment'
        },
        {
          id: 'signals-pain',
          value: pain_points,
          label: 'Pain Point Keywords'
        },
        {
          id: 'signals-solution',
          value: solution_research,
          label: 'Solution Research Keywords'
        }
      ];
      ai_suggestions = [
        {
          title: 'Pain Point Keywords',
          value: ai_suggestions_pain_71
        },
        {
          title: 'Solution Research Keywords',
          value: ai_suggestions_solution_71
        }
      ];
    } else if (revenueTargetingModelID === 106) {
      autocompletes = [
        {
          id: 'signals-pages',
          value: website_pages_106, 
          label: 'Website Pages'
        },
        {
          id: 'signals-competitors',
          value: our_competitors_106,
          label: 'Competitor Interest'
        },
        {
          id: 'signals-pain',
          value: pain_point_106,
          label: 'Pain Point Keywords'
        },
        {
          id: 'signals-solution',
          value: solution_research_106,
          label: 'Solution Research Keywords'
        }
      ];
      ai_suggestions = [
        {
          title: 'Pain Point Keywords',
          value: ai_suggestions_pain_106
        },
        {
          title: 'Solution Research Keywords',
          value: ai_suggestions_solution_106
        }
      ];
    } else if (revenueTargetingModelID === 107) {
      autocompletes = [
        {
          id: 'signals-pages',
          value: website_pages_107, 
          label: 'Website Pages'
        },
        // {
        //   id: 'signals-competitors',
        //   value: our_competitors_107,
        //   label: 'Competitor Interest'
        // },
        {
          id: 'signals-events',
          value: events_and_webinars_107,
          label: 'Events and Webinars'
        },
        {
          id: 'signals-jobs',
          value: functional_investment_107,
          label: 'Functional Investment'
        },
        {
          id: 'signals-pain',
          value: pain_point_107,
          label: 'Pain Point Keywords'
        },
        {
          id: 'signals-solution',
          value: solution_research_107,
          label: 'Solution Research Keywords'
        }
      ];
      ai_suggestions = [
        {
          title: 'Pain Point Keywords',
          value: ai_suggestions_pain_106
        },
        {
          title: 'Solution Research Keywords',
          value: ai_suggestions_solution_106
        }
      ];
    };

    let max_signals = 50;
    let signals_count = 0;
    autocompletes.forEach(auto => signals_count+=auto.value.length)
    
    return (
      <>
        <Toolbar sx={{ padding: "0 !important" }}>
          <Box flex={1}></Box>
          <LoadingButton
            id="deactivate-model-btn"
            onClick={handleDeactivate}
            loading={deactivateBtnLoading}
            variant="contained"
            color="primary"
            className={classes.deactivateButton}
          >
            Deactivate Model
          </LoadingButton>
          <LoadingButton
            onClick={handleSave}
            loading={saveBtnLoading}
            className={classes.saveButton}
            variant="contained"
          >
            Save Progress
          </LoadingButton>
        </Toolbar>
        <Stack spacing={3}>
          <Card>
            <Box sx={{p:"20px"}}>
              <Stack direction={"row"}>
                <Typography className={classes.sectionHeaderNoPadding} flex={1}>
                  Motivation Signals
                  <Tooltip
                    title={motivationSignalsTooltip}
                    arrow
                    placement="bottom-start"
                  >
                    <Info className={classes.infoIcon} color="primary" />
                  </Tooltip>
                </Typography>
                <Button
                  onClick={handleAiSuggestionsOpen}
                  variant="contained"
                  color="primary"
                >
                  View AI Suggestions
                </Button>
                <Modal open={aiOpen} onClose={() => setAiOpen(false)}>
                  <Box sx={createAiModalStyles}>
                    <Box sx={{position:"absolute", top:0, right:0, m:"12px"}}>
                      <IconButton
                        onClick={() => setAiOpen(false)}
                      >
                        <Cancel />
                      </IconButton>
                    </Box>
                    <Box sx={{overflow:"auto"}} height={"73vh"}>
                      
                      {ai_suggestions.map((item) => (
                        <>
                        <Typography variant="h5" color="primary" textAlign="center" pb={2}>{item.title}</Typography>
                          {item.value.map(value => (
                            <Toolbar>
                            <Typography flex={1} fontSize={14}>{value.title}</Typography>
                            {value.strength === 3 ? (
                                <Tooltip
                                  title={"Global Signal Strength: High"}
                                  arrow
                                  placement="bottom"
                                >
                                  <SignalCellular4Bar color='primary' sx={{mr:2}}/>
                                </Tooltip>
                              ) : value.strength === 2 ? (
                                <Tooltip
                                  title={"Global Signal Strength: Medium"}
                                  arrow
                                  placement="bottom"
                                >
                                  <SignalCellular3Bar color='primary' sx={{mr:2}}/>
                                </Tooltip>
                              ) : (
                                <Tooltip
                                  title={"Global Signal Strength: Low"}
                                  arrow
                                  placement="bottom"
                                >
                                  <SignalCellular1Bar color='primary' sx={{mr:2}}/>
                                </Tooltip>
                              )}
                              <Button
                                variant="contained"
                                color="primary"
                                sx={{mr:"12px", px:4}}
                                startIcon={<Add/>}
                              >
                                Add To Model
                              </Button>
                            </Toolbar>
                          ))}
                        </>
                      ))}
                    </Box>
                  </Box>
                </Modal>
              </Stack>
              <Typography className={classes.sectionHeaderNoPadding}>
                <Box>
                  {signals_count} / {max_signals}
                  <LinearProgress variant="determinate" value={signals_count / max_signals * 100} />
                </Box>
              </Typography>
            </Box>
            <Divider />
            <CardContent>
              <Box className={classes.section}>
                <Stack spacing={3}>
                  {autocompletes.map((autocomplete) => (
                    <Autocomplete
                      multiple
                      id={autocomplete.id}
                      options={[]}
                      value={autocomplete.value}
                      freeSolo
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          label={autocomplete.label}
                        />
                      )}
                      renderTags={(value) => {
                        return value.map((chip) => (
                          <Chip
                            key={chip.title}
                            label={
                              <Box display={"flex"} height={"22px"} justifyContent={"center"}>
                                {chip.title}
                                {chip.ai_suggested && <AIIcon/>}
                                {chip.strength}
                              </Box>
                            }
                            color={chip.color}
                            sx={{m:"3px"}}
                            onDelete={() => {}}
                          />
                        ))
                      }}
                    />
                  ))}
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Stack>
      </>
    )
  }

  const autocompletes = [
    {
      id: 'website_page',
      stateArray: websitePages,
      setStateArray: setWebsitePages,
      label: 'Website Pages'
    },
    {
      id: 'competitor_interest',
      stateArray: competitorInterest,
      setStateArray: setCompetitorInterest,
      label: 'Competitor Interest'
    },
    {
      id: 'functional_investment',
      stateArray: functionalInvestments,
      setStateArray: setFunctionalInvestments,
      label: 'Functional Investments'
    },
    {
      id: 'event_and_webinar',
      stateArray: eventsAndWebinars,
      setStateArray: setEventsAndWebinars,
      label: 'Events and Webinars'
    },
    {
      id: 'pain_point_keyword',
      stateArray: painPointKeywords,
      setStateArray: setPainPointKeywords,
      label: 'Pain Point Keywords'
    },
    {
      id: 'solution_research_keyword',
      stateArray: solutionResearchKeywords,
      setStateArray: setSolutionResearchKeywords,
      label: 'Solution Research Keywords'
    }
  ];

  const aiSuggestedMotivationSignalSets = [
    {
      id: 'website_page',
      title: 'Web Site Pages',
      stateArray: aiWebsitePages,
      setStateArray: setAiWebsitePages,
      targetSetStateArray: setWebsitePages
    },
    {
      id: 'competitor_interest',
      title: 'Competitor Interest',
      stateArray: aiCompetitorInterest,
      setStateArray: setAiCompetitorInterest,
      targetSetStateArray: setCompetitorInterest
    },
    {
      id: 'functional_investment',
      title: 'Functional Investment',
      stateArray: aiFunctionalInvestments,
      setStateArray: setAiFunctionalInvestments,
      targetSetStateArray: setFunctionalInvestments
    },
    {
      id: 'event_and_webinar',
      title: 'Events and Webinars',
      stateArray: aiEventsAndWebinars,
      setStateArray: setAiEventsAndWebinars,
      targetSetStateArray: setPainPointKeywords
    },
    {
      id: 'pain_point_keyword',
      title: 'Pain Point Keywords',
      stateArray: aiPainPointKeywords,
      setStateArray: setAiPainPointKeywords,
      targetSetStateArray: setPainPointKeywords
    },
    {
      id: 'solution_research_keyword',
      title: 'Solution Research Keywords',
      stateArray: aiSolutionResearchKeywords,
      setStateArray: setAiSolutionResearchKeywords,
      targetSetStateArray: setSolutionResearchKeywords
    }
  ];

  return (
    <>
      <Toolbar sx={{ padding: "0 !important" }}>
        <Box flex={1}></Box>
        {(auth.userAccountRole === "ADMIN" || auth.userAccountRole === "OWNER" || auth.superUser) && (
          <>
            {isModelActive ? (
              <LoadingButton
                id="deactivate-model-btn"
                onClick={handleDeactivate}
                loading={deactivateBtnLoading}
                variant="contained"
                color="primary"
                className={classes.deactivateButton}
              >
                Deactivate Model
              </LoadingButton>
            ) : (
              <>
                <Button
                  id="launch-model-btn"
                  onClick={handleOpen}
                  variant="contained"
                  color="primary"
                  className={classes.launchButton}
                >
                  Launch Model
                </Button>
                <Modal open={open} onClose={handleClose}>
                  <Box sx={createModalStyles}>
                    <Grid align="center">
                      <Typography className={classes.launchTitle}>
                        Launch <span style={{ color: "#009cf8" }}>{name}</span>
                      </Typography>
                      <Typography sx={{ margin: "12px 0" }}>
                        Note: Adding one additional active Revenue Targeting Model
                        will increase your monthly charge.
                      </Typography>
                    </Grid>
                    <Stack spacing={2}>
                      <Button
                        onClick={handleActivate}
                        loading={activateBtnLoading}
                        type="submit"
                        color="primary"
                        variant="contained"
                        fullWidth
                      >
                        Launch
                      </Button>
                      <Button
                        className={classes.cancelButton}
                        onClick={handleClose}
                        type="submit"
                        color="primary"
                        variant="contained"
                        fullWidth
                      >
                        Cancel
                      </Button>
                    </Stack>
                  </Box>
                </Modal>
              </>
            )}
            <LoadingButton
              onClick={handleSave}
              loading={saveBtnLoading}
              className={classes.saveButton}
              variant="contained"
            >
              Save Progress
            </LoadingButton>
          </>
        )}
        
      </Toolbar>
      <Stack spacing={3}>
        <Card>
          <Typography className={classes.sectionHeader}>
            Training Datasets
            <Tooltip
              title={trainingDatasetTooltip}
              arrow
              placement="bottom-start"
            >
              <Info className={classes.infoIcon} color="primary" />
            </Tooltip>
          </Typography>
          <Divider />
          <CardContent className={classes.trainingDatasetCardContent}>
            <Typography variant="body2">
              <Button variant="contained" component="label">
                Upload Training Dataset
                <input onInput={handleCSVChange} type="file" hidden />
              </Button>
              {trainingDatasets !== [] && (
                <List dense>
                  {trainingDatasets.map((trainingDataset) => (
                    <ListItem className={classes.trainingDatasetListItem} key={trainingDataset.id}>
                      <Link
                        className={classes.trainingDatasetLink}
                        underline="hover"
                        href={trainingDataset.url}
                      >
                        {trainingDataset.original_file_name}
                      </Link>
                      <IconButton
                        aria-label="remove"
                        edge="end"
                        onClick={() =>
                          handleRemoveTrainingDataset(trainingDataset.id)
                        }
                      >
                        <Cancel color="muted" />
                      </IconButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Typography>
          </CardContent>
        </Card>
        <Card>
          <Typography className={classes.sectionHeader}>
            Ideal Customer Profile
            <Tooltip
              title={companyFiltersTooltip}
              arrow
              placement="bottom-start"
            >
              <Info className={classes.infoIcon} color="primary" />
            </Tooltip>
          </Typography>
          <Divider />
          <CardContent>
            <Box className={classes.section}>
              <Stack spacing={3}>
                <Autocomplete
                  ChipProps={{
                    color: "primary",
                  }}
                  multiple
                  loading={definitionsLoading}
                  options={definitions.countries ?? []}
                  getOptionLabel={(country) => country}
                  value={countryFilters}
                  onChange={(event, newValue) => {
                    setCountryFilters(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Countries"
                    />
                  )}
                />
                <Autocomplete
                  ChipProps={{
                    color: "primary",
                  }}
                  multiple
                  loading={definitionsLoading}
                  options={definitions.business_types ?? []}
                  getOptionLabel={(businessType) => businessType}
                  value={companyTypeFilters}
                  onChange={(event, newValue) => {
                    setCompanyTypeFilters(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Company Type"
                    />
                  )}
                />
                <Autocomplete
                  ChipProps={{
                    color: "primary",
                  }}
                  multiple
                  loading={definitionsLoading}
                  options={definitions.industries ?? []}
                  getOptionLabel={(industry) => industry}
                  value={industryFilters}
                  onChange={(event, newValue) => {
                    setIndustryFilters(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Industries"
                    />
                  )}
                />
                <Stack direction={{ sm: 'column', md: 'row' }} spacing={{ sm: 2, md: 7 }}>
                  <Box sx={{width:1/1}}>
                    <Typography sx={{marginY: 1}}>Employee Bands</Typography>
                    <Slider
                      min={0}
                      max={employeeBandMarks.length-1}
                      step={1}
                      getAriaLabel={() => 'Employee band range'}
                      value={
                        [
                          employeeBandMarks.find((mark) => mark.band===employeeBandFilters[0])?.value || 0,
                          employeeBandMarks.find((mark) => mark.band===employeeBandFilters[employeeBandFilters.length-1])?.value+1 || definitions?.employee_bands?.length
                        ]
                      }
                      onChange={handleEmployeeBandChange}
                      valueLabelDisplay="off"
                      disableSwap
                      marks={employeeBandMarks}
                    />
                  </Box>
                  <Box sx={{width:1/1}}>
                    <Typography sx={{marginY: 1}}>Revenue Bands</Typography>
                    <Slider
                      min={0}
                      max={revenueBandMarks.length-1}
                      step={1}
                      getAriaLabel={() => 'Revenue band range'}
                      value={
                        [
                          revenueBandMarks?.find((mark) => mark.band===revenueBandFilters[0])?.value || 0,
                          revenueBandMarks?.find((mark) => mark.band===revenueBandFilters[revenueBandFilters.length-1])?.value+1 || definitions?.revenue_bands?.length
                        ]
                      }
                      onChange={handleRevenueSliderChange}
                      valueLabelDisplay="off"
                      disableSwap
                      marks={revenueBandMarks}
                    />
                  </Box>
                </Stack>
              </Stack>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <Typography className={classes.sectionHeader}>
            Ideal Buyer Persona
            <Tooltip
              title={contactFiltersTooltip}
              arrow
              placement="bottom-start"
            >
              <Info className={classes.infoIcon} color="primary" />
            </Tooltip>
          </Typography>
          <Divider />
          <CardContent>
            <Box className={classes.section}>
              <Stack spacing={3}>
                <Autocomplete
                  ChipProps={{
                    color: "primary",
                  }}
                  multiple
                  id="contact-seniorities"
                  loading={definitionsLoading}
                  options={definitions.contact_seniorities ?? []}
                  getOptionLabel={(seniority) => seniority}
                  value={contactSeniorityFilters}
                  onChange={(event, newValue) => {
                    setContactSeniorityFilters(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Seniority"
                    />
                  )}
                />
                <Autocomplete
                  ChipProps={{
                    color: "primary",
                  }}
                  multiple
                  id="contact-titles"
                  options={[]}
                  getOptionLabel={(option) => option}
                  loading={definitionsLoading}
                  value={contactTitleFilters}
                  freeSolo
                  onChange={(event, newValue) => {
                    setContactTitleFilters(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Title"
                    />
                  )}
                />
                <Autocomplete
                  ChipProps={{
                    color: "primary",
                  }}
                  multiple
                  id="contact-functions"
                  loading={definitionsLoading}
                  options={definitions.contact_functions ?? []}
                  getOptionLabel={(func) => func}
                  value={contactFunctionFilters}
                  onChange={(event, newValue) => {
                    setContactFunctionFilters(newValue);
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="standard"
                      label="Function"
                    />
                  )}
                />
              </Stack>
            </Box>
          </CardContent>
        </Card>
        <Card>
          <Box sx={{p:"20px"}}>
            <Stack direction={"row"}>
              <Typography className={classes.sectionHeaderNoPadding} flex={1}>
                Motivation Signals
                <Tooltip
                  title={motivationSignalsTooltip}
                  arrow
                  placement="bottom-start"
                >
                  <Info className={classes.infoIcon} color="primary" />
                </Tooltip>
              </Typography>
              <Button
                onClick={handleAiSuggestionsOpen}
                variant="contained"
                color="primary"
              >
                View AI Suggestions
              </Button>
              <Modal open={aiOpen} onClose={() => setAiOpen(false)}>
                <Box sx={createAiModalStyles}>
                  <Box sx={{position:"absolute", top:0, right:0, m:"12px"}}>
                    <IconButton
                      onClick={() => setAiOpen(false)}
                    >
                      <Cancel />
                    </IconButton>
                  </Box>
                  <Box sx={{overflow:"auto"}} height={"73vh"}>
                    {aiSuggestedMotivationSignalSets.map(set => (
                      <>
                        {set.stateArray.length > 0 && (
                          <>
                            <Typography variant="h5" color="primary" textAlign="center" py={2}>{set.title}</Typography>
                            {set.stateArray.map(suggestion => (
                              <Toolbar>
                              <Typography flex={1} fontSize={14}>{suggestion.value}</Typography>
                              {suggestion.global_strength > 500 ? (
                                  <Tooltip
                                    title={"Global Signal Strength: High"}
                                    arrow
                                    placement="bottom"
                                  >
                                    <SignalCellular4Bar color='primary' sx={{mr:2}}/>
                                  </Tooltip>
                                ) : suggestion.global_strength > 100 ? (
                                  <Tooltip
                                    title={"Global Signal Strength: Medium"}
                                    arrow
                                    placement="bottom"
                                  >
                                    <SignalCellular3Bar color='primary' sx={{mr:2}}/>
                                  </Tooltip>
                                ) : suggestion.global_strength > 0 ? (
                                  <Tooltip
                                    title={"Global Signal Strength: Low"}
                                    arrow
                                    placement="bottom"
                                  >
                                    <SignalCellular1Bar color='primary' sx={{mr:2}}/>
                                  </Tooltip>
                                ) : suggestion.global_strength === 0 ? (
                                  <Tooltip
                                    title={"Global Signal Strength: Zero"}
                                    arrow
                                    placement="bottom"
                                  >
                                    <SignalCellularNull color='primary' sx={{mr:2}}/>
                                  </Tooltip>
                                ) : (
                                  <Tooltip
                                    title={"Calculating Global Strength"}
                                    arrow
                                    placement="bottom"
                                  >
                                    <PermDataSetting color='primary' sx={{mr:2}}/>
                                  </Tooltip>
                                )}
                                <Button
                                  variant="contained"
                                  color="primary"
                                  sx={{mr:"12px", px:4}}
                                  onClick={() => handleAddAiSignal(suggestion, set.targetSetStateArray, set.setStateArray)}
                                  startIcon={<Add/>}
                                >
                                  Add To Model
                                </Button>
                              </Toolbar>
                            ))}
                          </>    
                        )}
                      </>
                    ))}
                  </Box>
                </Box>
              </Modal>
            </Stack>
            <Typography className={classes.sectionHeaderNoPadding}>
              {(websitePages.length + competitorInterest.length + functionalInvestments.length + eventsAndWebinars.length + painPointKeywords.length + solutionResearchKeywords.length <= maxSignals) ? 
                (
                  <Box>
                    {websitePages.length + competitorInterest.length + functionalInvestments.length + eventsAndWebinars.length + painPointKeywords.length + solutionResearchKeywords.length} / {maxSignals}
                    <LinearProgress variant="determinate" value={(websitePages.length + competitorInterest.length + functionalInvestments.length + eventsAndWebinars.length + painPointKeywords.length + solutionResearchKeywords.length) / maxSignals * 100} />
                  </Box>
                ) : (
                  <Box>
                    <span style={{color:"#d32f2f"}}>{websitePages.length + competitorInterest.length + functionalInvestments.length + eventsAndWebinars.length + painPointKeywords.length + solutionResearchKeywords.length}</span> / {maxSignals}
                    <LinearProgress variant="determinate" value={100} color="error"/>
                  </Box>
                )
              }
            </Typography>
          </Box>
          <Divider />
          <CardContent>
            <Box className={classes.section}>
              <Stack spacing={3}>
                {autocompletes.map(autocomplete => (
                  <Autocomplete
                    multiple
                    id={autocomplete.id}
                    options={[]}
                    value={autocomplete.stateArray}
                    freeSolo
                    onChange={(event, newValue) => {
                      autocomplete.setStateArray((currentArray) => [...currentArray, {value: newValue[newValue.length-1], type: autocomplete.id, ai_suggested: false, global_strength:null}]);
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        variant="standard"
                        label={autocomplete.label}
                      />
                    )}
                    renderTags={(value, _, ownerState) => {
                      if (ownerState.id === 'website_page') {
                        return value.map((signal) => (
                          <Chip
                            key={signal.value}
                            label={signal.value}
                            color={'primary'}
                            onDelete={() => autocomplete.setStateArray((currentArray) => currentArray.filter((currentArrayElement) => currentArrayElement.value !== signal.value))}
                            sx={{m:"3px"}}
                          />
                        ))
                      }
                      return value.map((signal) => (
                        <Chip
                          key={signal.value}
                          label={
                            <Box display={"flex"} height={"22px"} justifyContent={"center"}>
                              {signal.value}
                              {signal.ai_suggested && <AIIcon/>}
                              {signal.global_strength === null ? (
                                <CalculatingSignalStrength/>
                              ) : signal.global_strength === 0 ? (
                                <ZeroSignalStrength/>
                              ) : signal.global_strength < 100 ? (
                                <LowSignalStrength/>
                              ) : signal.global_strength < 500 ? (
                                <MediumSignalStrength/>
                              ) : (
                                <HighSignalStrength/>
                              )}
                            </Box>
                          }
                          color={
                            mostRecentMotivationSignals.includes(signal.value.toLowerCase()) ? "performantSignal"
                            : presentMotivationSignals.includes(signal.value.toLowerCase()) ? "presentSignal" : "neutralDark"
                          }
                          onDelete={() => autocomplete.setStateArray((currentArray) => currentArray.filter((currentArrayElement) => currentArrayElement.value !== signal.value))}
                          sx={{m:"3px"}}
                        />
                      ))
                    }}
                  />
                ))}
              </Stack>
            </Box>
          </CardContent>
        </Card>
      </Stack>
    </>
  );
}
